import { Injectable, inject } from '@angular/core';
import { Observable, of, tap, map } from 'rxjs';
import { DataService } from '@core/services/data.service';
import { CourseStatus } from '@core/enums/course-status';

export interface IModuleProgress {
  isCompleted: boolean;
  completedAt?: string;
}

interface IModuleProgressResponse {
  id: number;
  courseId: number;
  moduleId: string;
  isStarted: boolean;
  isCompleted: boolean;
  completedAt?: string;
}

@Injectable({ providedIn: 'root' })
export class ModuleProgressService {
  private dataService = inject(DataService);

  // In-memory cache keyed by "courseId.moduleId"
  private cache = new Map<string, IModuleProgressResponse>();
  private loadedCourses = new Set<number>();

  private cacheKey(courseId: number, moduleId: string): string {
    return `${courseId}.${moduleId}`;
  }

  /**
   * Loads all module progress for a course from the API into the local cache.
   * Components should call this in ngOnInit before using synchronous methods.
   */
  loadCourseProgress(courseId: number, forceReload = false): Observable<void> {
    // If already loaded in this session, trust the cache to avoid
    // race conditions with in-flight save requests.
    if (!forceReload && this.loadedCourses.has(courseId)) {
      return of(void 0);
    }

    return this.dataService.get(`Module/progress/course/${courseId}`, undefined, false).pipe(
      tap((items: IModuleProgressResponse[]) => {
        // Clear existing cache for this course
        this.cache.forEach((_, key) => {
          if (key.startsWith(`${courseId}.`)) this.cache.delete(key);
        });
        items.forEach(item => {
          this.cache.set(this.cacheKey(item.courseId, item.moduleId), item);
        });
        this.loadedCourses.add(courseId);
      }),
      map(() => void 0)
    );
  }

  markCourseStarted(courseId: number): void {
    // No-op for API — course "started" is inferred from having any progress records.
    // This preserves the existing call sites without breaking anything.
  }

  hasCourseStarted(courseId: number): boolean {
    for (const [key, value] of this.cache) {
      if (key.startsWith(`${courseId}.`) && value.isStarted) return true;
    }
    return false;
  }

  markModuleStarted(courseId: number, moduleId: string): void {
    const key = this.cacheKey(courseId, moduleId);
    const existing = this.cache.get(key);
    if (existing && existing.isStarted) return;

    // Update cache immediately
    this.cache.set(key, {
      ...(existing || { id: 0, courseId, moduleId, isCompleted: false }),
      isStarted: true
    });

    // Fire API call
    this.dataService.post('Module/progress/start', { courseId, moduleId })
      .subscribe();
  }

  hasModuleStarted(courseId: number, moduleId: string): boolean {
    return this.cache.get(this.cacheKey(courseId, moduleId))?.isStarted ?? false;
  }

  getModuleStatus(courseId: number, moduleId: string): CourseStatus {
    if (this.getProgress(courseId, moduleId).isCompleted) {
      return CourseStatus.Completed;
    }
    if (this.hasModuleStarted(courseId, moduleId)) {
      return CourseStatus.Incomplete;
    }
    return CourseStatus.NotStarted;
  }

  resetCourse(courseId: number, moduleIds: string[]): void {
    // Clear cache
    moduleIds.forEach(moduleId => {
      this.cache.delete(this.cacheKey(courseId, moduleId));
    });
    this.loadedCourses.delete(courseId);

    // Fire API call
    this.dataService.delete(`Module/progress/course/${courseId}/reset`)
      .subscribe();
  }

  getCourseStatus(courseId: number, moduleIds: string[]): CourseStatus {
    if (this.isCourseComplete(courseId, moduleIds)) {
      return CourseStatus.Completed;
    }
    const hasAnyCompleted = this.getCompletedCount(courseId, moduleIds) > 0;
    if (this.hasCourseStarted(courseId) || hasAnyCompleted) {
      return CourseStatus.Incomplete;
    }
    return CourseStatus.NotStarted;
  }

  getProgress(courseId: number, moduleId: string): IModuleProgress {
    const cached = this.cache.get(this.cacheKey(courseId, moduleId));
    if (!cached) return { isCompleted: false };
    return { isCompleted: cached.isCompleted, completedAt: cached.completedAt };
  }

  saveProgress(courseId: number, moduleId: string, progress: IModuleProgress): void {
    const key = this.cacheKey(courseId, moduleId);
    const existing = this.cache.get(key);

    // Update cache
    this.cache.set(key, {
      ...(existing || { id: 0, courseId, moduleId, isStarted: true }),
      isCompleted: progress.isCompleted,
      completedAt: progress.completedAt
    });

    // Fire API call
    if (progress.isCompleted) {
      this.dataService.post('Module/progress/complete', { courseId, moduleId })
        .subscribe();
    }
  }

  isCourseComplete(courseId: number, moduleIds: string[]): boolean {
    return moduleIds.every(id => this.getProgress(courseId, id).isCompleted);
  }

  getCompletedCount(courseId: number, moduleIds: string[]): number {
    return moduleIds.filter(id => this.getProgress(courseId, id).isCompleted).length;
  }
}
